import { Link } from "react-router-dom";
import { type DbProduct, getProductFinalPrice, getProductImage } from "@/lib/supabase";

type Props = {
  product: DbProduct;
};

export default function ProductCard({ product: p }: Props) {
  const img = getProductImage(p);
  const finalPrice = getProductFinalPrice(p);
  const hasDiscount = p.discount_enabled && p.discount_price != null;
  const detailUrl = `/product/${p.id}`;
  const soldOut = (p.stock ?? 0) === 0;

  return (
    <article
      data-product-shop
      className="group flex flex-col transition-transform duration-500 ease-out hover:-translate-y-1"
    >
      <Link
        to={detailUrl}
        className="relative aspect-square overflow-hidden bg-background-100 rounded-lg block cursor-pointer"
      >
        {img && (
          <img
            src={img}
            alt={p.name}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08] ${
              soldOut ? "opacity-60" : ""
            }`}
          />
        )}
        {/* Subtle overlay on hover for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {soldOut && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-background-50 text-foreground-950 text-[10px] tracking-widest uppercase">
            Sold out
          </span>
        )}
        {!soldOut && hasDiscount && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-primary-500 text-background-50 text-[10px] tracking-widest uppercase">
            Sale
          </span>
        )}

        {/* Quick-look arrow that slides up on hover */}
        <span className="absolute bottom-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-background-50 text-foreground-950 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400 ease-out">
          <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
        </span>
      </Link>
      <Link to={detailUrl} className="pt-3 cursor-pointer">
        <h3 className="text-sm font-medium text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
          {p.name}
        </h3>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-sm text-foreground-800">${finalPrice.toFixed(2)}</span>
          {hasDiscount && (
            <span className="text-xs text-foreground-500 line-through">
              ${Number(p.price).toFixed(2)}
            </span>
          )}
        </div>
      </Link>
    </article>
  );
}